import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search, UserPlus, Phone, Check, AlertTriangle, Send, Users, Building2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { validatePhoneNumber, formatPhoneForDisplay, cleanPhoneNumber } from '../utils/phoneValidation';
import { API, getApiUrl } from '../utils/api';

const AddMemberModal = ({ 
  isOpen, 
  onClose, 
  projectId,
  currentUser,
  existingMembers = [],
  onMemberAdded 
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [availableContacts, setAvailableContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Phone number validation state
  const [phoneValidation, setPhoneValidation] = useState(null);
  const [showPhoneConfirm, setShowPhoneConfirm] = useState(false);
  const [pendingPhone, setPendingPhone] = useState(null);
  
  // SMS invite state
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);

  // Load available contacts on mount
  useEffect(() => {
    if (isOpen && currentUser?.id) {
      loadAvailableContacts();
    }
  }, [isOpen, currentUser?.id]);

  // Filter contacts based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredContacts(availableContacts);
      setPhoneValidation(null);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    
    // Check if input looks like a phone number
    const isPhoneQuery = /^[+\d\s\-()]+$/.test(query) && query.replace(/\D/g, '').length >= 6;
    
    if (isPhoneQuery) {
      // Validate phone number
      const validation = validatePhoneNumber(searchQuery);
      setPhoneValidation(validation);
      
      // Also search in contacts by phone
      const phoneFiltered = availableContacts.filter(c => 
        c.phone_number?.replace(/\D/g, '').includes(query.replace(/\D/g, ''))
      );
      setFilteredContacts(phoneFiltered);
    } else {
      // Search by name or company
      const filtered = availableContacts.filter(c =>
        c.name?.toLowerCase().includes(query) ||
        c.company?.toLowerCase().includes(query)
      );
      setFilteredContacts(filtered);
      setPhoneValidation(null);
    }
  }, [searchQuery, availableContacts]);

  const loadAvailableContacts = async () => {
    setLoading(true);
    try {
      // Fetch contacts: same company + shared projects
      const response = await fetch(`${getApiUrl()}/users/${currentUser.id}/contacts`);
      if (response.ok) {
        const contacts = await response.json();
        // Filter out existing members (check both id and user_id for compatibility)
        const existingIds = existingMembers.map(m => m.user_id || m.id);
        const available = contacts.filter(c => !existingIds.includes(c.id) && c.id !== currentUser.id);
        setAvailableContacts(available);
        setFilteredContacts(available);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneConfirm = () => {
    if (phoneValidation?.suggestedInternational) {
      setSearchQuery(phoneValidation.suggestedInternational);
      setShowPhoneConfirm(false);
      setPendingPhone(phoneValidation.suggestedInternational);
      
      // Re-validate with corrected number
      const newValidation = validatePhoneNumber(phoneValidation.suggestedInternational);
      setPhoneValidation(newValidation);
    }
  };

  const handleAddByPhone = async () => {
    const phone = pendingPhone || (phoneValidation?.valid ? cleanPhoneNumber(searchQuery) : null);
    if (!phone) return;

    setLoading(true);
    try {
      // Check if user exists
      const checkResponse = await fetch(`${getApiUrl()}/users/by-phone/${encodeURIComponent(phone)}`);
      
      if (checkResponse.ok) {
        // User exists - add directly
        const user = await checkResponse.json();
        await addMemberToProject(user);
      } else if (checkResponse.status === 404) {
        // User doesn't exist - offer SMS invite
        setSelectedUser(null);
        setInviteResult({ 
          type: 'not_found', 
          phone: phone,
          message: t('team.notRegistered', 'This number is not yet registered with Babelmix.')
        });
      }
    } catch (error) {
      console.error('Error checking phone:', error);
      setInviteResult({ type: 'error', message: t('common.searchError', 'Error searching') });
    } finally {
      setLoading(false);
    }
  };

  const handleSendSMSInvite = async () => {
    const phone = inviteResult?.phone;
    if (!phone) return;

    setSendingInvite(true);
    try {
      const response = await fetch(`${getApiUrl()}/invites/sms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: phone,
          project_id: projectId,
          invited_by: currentUser.id,
          invited_by_name: currentUser.name
        })
      });

      if (response.ok) {
        const result = await response.json();
        setInviteResult({
          type: 'success',
          message: t('team.smsSent', 'SMS invitation sent to {{phone}}!').replace('{{phone}}', formatPhoneForDisplay(phone))
        });
        
        // Close modal after delay
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        const error = await response.json();
        setInviteResult({
          type: 'error',
          message: error.detail || t('common.errorSendingSMS', 'Error sending SMS')
        });
      }
    } catch (error) {
      console.error('Error sending SMS invite:', error);
      setInviteResult({
        type: 'error',
        message: t('team.sendError', 'Verbindungsfehler beim Senden')
      });
    } finally {
      setSendingInvite(false);
    }
  };

  const addMemberToProject = async (user) => {
    try {
      // Backend expects user_id as query parameter
      const response = await fetch(`${getApiUrl()}/projects/${projectId}/members?user_id=${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        if (onMemberAdded) onMemberAdded(user);
        handleClose();
      } else {
        const errorData = await response.json();
        const errorMessage = typeof errorData.detail === 'string' 
          ? errorData.detail 
          : (errorData.detail?.msg || errorData.message || t('team.addError', 'Fehler beim Hinzufügen'));
        alert(errorMessage);
      }
    } catch (error) {
      console.error('Error adding member:', error);
      alert(t('team.addMemberError', 'Fehler beim Hinzufügen des Mitglieds') + ': ' + (error.message || t('common.unknownError', 'Unbekannter Fehler')));
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    setSelectedUser(null);
    setPhoneValidation(null);
    setShowPhoneConfirm(false);
    setPendingPhone(null);
    setInviteResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              <h2 className="font-bold">{t('team.addMember', 'Mitglied hinzufügen')}</h2>
            </div>
            <button onClick={handleClose} className="p-1 hover:bg-white/20 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('team.searchPlaceholder', 'Name, Firma oder Telefonnummer...')}
              className="pl-10"
            />
          </div>
          
          {/* Phone validation feedback */}
          {phoneValidation?.needsConfirmation && (
            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-yellow-800 break-words">{phoneValidation.message}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" onClick={handlePhoneConfirm} className="bg-yellow-600 hover:bg-yellow-700">
                      <Check className="w-4 h-4 mr-1" />
                      {t('team.yesCorrect', 'Ja, korrigieren')}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setPhoneValidation(null)}>
                      {t('common.cancel', 'Abbrechen')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {phoneValidation?.valid && (
            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Phone className="w-4 h-4 text-green-600 flex-shrink-0" />
                  <span className="text-sm text-green-800 truncate">
                    {phoneValidation.display}
                    {phoneValidation.country && <span className="text-green-600 ml-1">({phoneValidation.country})</span>}
                  </span>
                </div>
                <Button size="sm" onClick={handleAddByPhone} disabled={loading} className="w-full sm:w-auto">
                  {t('common.search', 'Suchen')}
                </Button>
              </div>
            </div>
          )}
          
          {phoneValidation?.error && !phoneValidation.needsConfirmation && (
            <p className="mt-2 text-sm text-red-600">{phoneValidation.error}</p>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* SMS Invite Result */}
          {inviteResult && (
            <div className={`mb-4 p-4 rounded-lg ${
              inviteResult.type === 'success' ? 'bg-green-50 border border-green-200' :
              inviteResult.type === 'not_found' ? 'bg-blue-50 border border-blue-200' :
              'bg-red-50 border border-red-200'
            }`}>
              {inviteResult.type === 'success' && (
                <div className="flex items-center gap-2 text-green-700">
                  <Check className="w-5 h-5" />
                  <span>{inviteResult.message}</span>
                </div>
              )}
              
              {inviteResult.type === 'not_found' && (
                <div>
                  <p className="text-blue-800 mb-3">{inviteResult.message}</p>
                  <Button 
                    onClick={handleSendSMSInvite} 
                    disabled={sendingInvite}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {sendingInvite ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Sende...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        SMS-Einladung senden
                      </>
                    )}
                  </Button>
                </div>
              )}
              
              {inviteResult.type === 'error' && (
                <div className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="w-5 h-5" />
                  <span>{inviteResult.message}</span>
                </div>
              )}
            </div>
          )}

          {/* Contact List */}
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : filteredContacts.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 mb-2">
                <Users className="w-3 h-3 inline mr-1" />
                Verfügbare Kontakte ({filteredContacts.length})
              </p>
              {filteredContacts.map((contact) => (
                <button
                  key={contact.id}
                  onClick={() => addMemberToProject(contact)}
                  className="w-full p-3 bg-gray-50 hover:bg-blue-50 rounded-lg flex items-center gap-3 transition-colors text-left"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-medium">
                      {contact.name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{contact.name}</p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {contact.company && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {contact.company}
                        </span>
                      )}
                      {contact.shared_projects > 0 && (
                        <Badge variant="secondary" className="text-xs">
                          {contact.shared_projects} {t('team.sharedProjects', 'shared projects')}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <UserPlus className="w-5 h-5 text-blue-600" />
                </button>
              ))}
            </div>
          ) : searchQuery ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-2">{t('team.noContactsFound', 'Keine Kontakte gefunden')}</p>
              {phoneValidation?.valid && (
                <p className="text-sm text-blue-600">
                  {t('team.clickSearchHint', 'Klicken Sie auf "Suchen", um nach dieser Nummer zu suchen')}
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-2 text-gray-300" />
              <p>{t('team.searchHint', 'Suchen Sie nach Namen, Firma oder Telefonnummer')}</p>
            </div>
          )}
        </div>

        {/* Footer hint */}
        <div className="p-3 border-t bg-gray-50 text-xs text-gray-500 text-center">
          <Phone className="w-3 h-3 inline mr-1" />
          {t('team.phoneHint', 'Tipp: Telefonnummer im Format +49 151 12345678 eingeben')}
        </div>
      </div>
    </div>
  );
};

export default AddMemberModal;
